import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { RankingRuleActionType } from 'types/ranking';
dayjs.extend(utc);

/**
 * Style-color comes in both numeric and alphanumeric. Example: 315115-112,00037923X-BX1,AJ1134-001,APV8-100,etc
 * Style-code comes in both numeric and alphanumeric. Example: 315115,FA20NKAF10A,N19900AMQM,N0002004,AQ5604,etc
 * regex patterns condition:
 * style:
 * - min 4 and max 11 character in style. Ex. 00037923X
 * color:
 * - hyphen(-)
 * - max 3 character in color. Ex. BX1
 */
export const styleColorValidator = (styleColor: string) =>
  /^[0-9a-zA-Z]{4,11}-[0-9a-zA-Z]{3}$/.test(styleColor);

export const styleCodeValidator = (styleCode: string) =>
  /^[0-9a-zA-Z]{4,11}$/.test(styleCode);

export const pbidValidator = (pbid: string) => /^[0-9a-zA-Z]{3,20}$/.test(pbid);

export const priceValueValidator = (price: string) =>
  /^\d+(\.\d+)?$/.test(price);

export const processTextContent = (textContent: string) =>
  textContent.replace(/[\n\r]/g, '');

export const processCommaSeparatedStringValues = (textInput: string) => {
  return textInput
    .replace(/[\n\r]/g, ',')
    .split(',')
    .filter((s) => s !== '')
    .map((t) => t.trim());
};

export const swapArrayItem = (idx1: number, idx2: number, list: any[]) => {
  if (
    idx1 === idx2 ||
    idx1 < 0 ||
    idx2 < 0 ||
    idx1 > list.length - 1 ||
    idx2 > list.length - 1
  ) {
    return list;
  }
  const a = list[idx1];
  const b = list[idx2];
  return R.pipe(R.set(R.lensIndex(idx1), b), R.set(R.lensIndex(idx2), a))(list);
};

export const localTimeToUTC = (time: string) => {
  return new Date(time).toISOString();
};
export const utcTimeToLocalTime = (time: string) => {
  return dayjs(time).local().format('YYYY-MM-DDTHH:mm');
};

export const channelOptions = [
  { value: '82a74ac1-c527-4470-b7b0-fb5f3ef3c2e2', label: 'Nike App' },
  { value: 'd9a5bc42-4b9c-4976-858a-f159cf99c647', label: 'Nike.COM' },
  { value: '15d92135-394e-487a-8afa-f6c8525c9ea9', label: 'WeChat' },
  { value: '008be467-6c78-4079-94f0-70e2d6cc4003', label: 'SNKRS' },
];

export const marketplaceOptions = [{ value: 'CN', label: 'Greater China' }];
export const navigationTypeOptions = [
  { value: 'BROWSE', label: 'BROWSE' },
  { value: 'SEARCH', label: 'SEARCH' },
];

export const strategyOptions = [
  {
    value: 'CLICKS_SIGNAL_STRATEGY',
    label: '	Ranking by clicks',
    note: '按点击排名',
  },
  {
    value: 'ADDS_TO_CART_SIGNAL_STRATEGY',
    label: 'Ranking by addsToCart',
    note: '按加车排名',
  },
  {
    value: 'PURCHASES_SIGNAL_STRATEGY',
    label: 'Ranking by purchases',
    note: '按购买排名',
  },
  {
    value: 'REVENUE_SIGNAL_STRATEGY',
    label: 'Ranking by revenue',
    note: '按收入排名（目前缺乏数据，所以没有效果，不要使用）',
  },
  {
    value: 'NEWNESS_SIGNAL_STRATEGY',
    label: 'Ranking by product publishStartDate',
    note: '按产品发布（publishStartDate）时间',
  },
  {
    value: 'FULL_PRICE_SIGNAL_STRATEGY',
    label: 'Ranking by full price product',
    note: '按全价产品排名',
  },
  {
    value: 'DISCOUNT_PRICE_SIGNAL_STRATEGY',
    label: 'Ranking by discount price product',
    note: '按打折价产品排名',
  },
  {
    value: 'SKU_INVENTORY_RATIO_HIGH_SIGNAL_STRATEGY',
    label: 'Ranking by high inventory product',
    note: '按高库存产品排名',
  },
  {
    value: 'SKU_INVENTORY_RATIO_LOW_SIGNAL_STRATEGY',
    label: 'Ranking by low inventory product',
    note: '按低库存产品排名',
  },
  {
    value: 'SEASONAL_TOP_SIGNAL_STRATEGY',
    label: 'Ranking by seasonal top product',
    note: '按当季热销产品排名',
  },
  {
    value: 'GENDER_SIGNAL_STRATEGY',
    label: 'Ranking by gender',
    note: '按性别排名（男-Unisex-女-童）',
  },
];

export const createRankingRulePayload = (data: {
  _id?: string;
  ruleName: string;
  consumerChannels: typeof channelOptions;
  navigationType: typeof navigationTypeOptions;
  triggerModal: {
    visible: boolean;
    type: string;
    cascadingConcepts: RankingRuleTriggerConcept[][];
    exactConcepts: RankingRuleTriggerConcept[][];
    exactKeywords: string[];
    phraseKeywords: string[];
    partialKeywords: string[];
  };
  targetModal: {
    visible: boolean;
    type: string;
    actionType: RankingRuleActionType;
    boosts: RankingRuleTargetBoostItem[];
    buries: RankingRuleTargetBuryItem[];
  };
  isApplyToAll: boolean;
  startDate: string;
  endDate: string;
  strategy: (typeof strategyOptions)[number];
  priority: number;
}) => {
  const {
    ruleName,
    consumerChannels,
    navigationType,
    triggerModal,
    targetModal,
    isApplyToAll,
    startDate,
    endDate,
    strategy,
    priority,
  } = data;
  const consumerChannelsVal = consumerChannels.map((cc) => ({
    name: cc.label,
    id: cc.value,
  }));
  const navigationTypeVal = navigationType.map((nt) => nt.value);
  const cascadingConcepts = triggerModal.cascadingConcepts.map((cc) => ({
    mode: 'CASCADE',
    classificationValues: cc.map((c) => c.id),
  }));
  const exactConcepts = triggerModal.exactConcepts.map((cc) => ({
    mode: 'EXACT_LOCATION',
    classificationValues: cc.map((c) => c.id),
  }));
  const exactKeywords = triggerModal.exactKeywords.map((ek) => ({
    mode: 'EXACT',
    searchTerm: ek,
  }));
  const partialKeywords = triggerModal.partialKeywords.map((ek) => ({
    mode: 'PARTIAL',
    searchTerm: ek,
  }));
  const triggerData: any = {
    context: {
      marketplaces: ['CN'],
      consumerChannels: consumerChannelsVal,
      navigationType: navigationTypeVal,
    },
  };
  if (isApplyToAll) {
    triggerData.applyToAll = true;
  } else {
    triggerData.queries = {
      classifications: cascadingConcepts.concat(exactConcepts),
      keywords: exactKeywords.concat(partialKeywords),
    };
  }
  const payload: RankingRule = {
    priority,
    startDate: localTimeToUTC(startDate),
    endDate: localTimeToUTC(endDate),
    name: ruleName,
    strategy: strategy.value,
    trigger: triggerData,
    boosts: targetModal.boosts.map((boost) => {
      const data: RankingRuleTargetBoostItem = {
        type: boost.type,
        relation: boost.relation,
      };
      if (boost.value) {
        data.value = boost.value;
        return data;
      }
      if (boost.multiValue) {
        data.multiValue = boost.multiValue;
        return data;
      }
      return data;
    }),
    buries: targetModal.buries.map((bury) => {
      const data: RankingRuleTargetBuryItem = {
        type: bury.type,
        relation: bury.relation,
      };
      if (bury.value) {
        data.value = bury.value;
        return data;
      }
      if (bury.multiValue) {
        data.multiValue = bury.multiValue;
        return data;
      }
      return data;
    }),
  };
  if (data._id) {
    payload._id = data._id;
  }
  return payload;
};
